import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import loadable from '@loadable/component';
import BaseButton from '../base-button';
import Modal from '../../modal';
import {
  openAlerts,
  removeAlertRequest,
} from '../../../areas/sidebar/actions/alert';
import ModalActions, { showModal } from '../../modal/actions';
import { PRODUCT_ALERT_MODAL_ID } from '../../../areas/product/constants';
import { useIsAuthenticated } from '../../../hooks/use-is-authenticated';
import { AppState } from '../../../reducers';
import { CurrentModal } from '../../../areas/product/types';
import { Product } from '../../../types';
import { UserProductAlert } from '../../../areas/sidebar/types';

import * as S from '../styles';
import { IconNotificationFilled } from '@prisjakt/ui/icon/notification-filled';
import { IconNotificationOutlined } from '@prisjakt/ui/icon/notification-outlined';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../../../areas/product-alert/components/product-alert-modal-content'
  )
);

type Props = {
  product: Product;
  layout?: 'button' | 'icon';
};

const AlertButton = ({ product, layout = 'icon' }: Props) => {
  const { t, market } = useAppContext();
  const dispatch = useDispatch();
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const userProductAlerts = useSelector(
    (state: AppState) =>
      state.areas.sidebar.alerts.products as UserProductAlert[]
  );
  const userAlert = userProductAlerts.find(
    alert => alert.productId === product.id
  );
  const alertId = userAlert?.alertId;
  const currentModal: CurrentModal = useSelector(
    (state: AppState) => state.modal.current
  );

  const showAlertModal = useCallback(() => {
    setOpenAlertModal(true);
    dispatch(showModal(PRODUCT_ALERT_MODAL_ID));
  }, [dispatch]);

  const hideAlertModal = useCallback(() => {
    setOpenAlertModal(false);
    dispatch(ModalActions.hideModal());
  }, [dispatch]);

  const removeAlert = useCallback(
    (id: number, productId: number) => {
      if (!id) return;
      dispatch(openAlerts());
      dispatch(removeAlertRequest(id, productId));
    },
    [dispatch]
  );

  const editAlert = useCallback(() => {
    dispatch(openAlerts());
    showAlertModal();
  }, [dispatch, showAlertModal]);

  if (!product) return null;

  return (
    <>
      {openAlertModal && currentModal === PRODUCT_ALERT_MODAL_ID && (
        <Modal
          id={PRODUCT_ALERT_MODAL_ID}
          onRequestClose={hideAlertModal}
          width={500}
        >
          <ProductAlertModalContent
            productId={product.id}
            hideModal={hideAlertModal}
            privacyPolicyUri={market?.privacySettings?.responsive?.privacyUrl}
          />
        </Modal>
      )}
      {alertId && isAuthenticated ? (
        <S.Dropdown
          variant="secondary"
          grow="left"
          showArrow={false}
          toggleElement={
            <BaseButton
              isActive
              icon={<IconNotificationFilled />}
              ariaLabel={t('product', 'alert.create-button')}
              tooltip={t('product', 'action-button.alert-tooltip')}
            />
          }
        >
          <S.DropdownItem onClick={() => removeAlert(alertId, product.id)}>
            {t('product', 'alert.delete-button')}
          </S.DropdownItem>
          <S.DropdownItem onClick={editAlert}>
            {t('product', 'alert.edit-button')}
          </S.DropdownItem>
        </S.Dropdown>
      ) : (
        <BaseButton
          isActive={false}
          icon={<IconNotificationOutlined />}
          ariaLabel={t('alerts', 'alert-form.create-button')}
          onClick={showAlertModal}
          tooltip={t('product', 'action-button.alert-tooltip')}
          layout={layout}
        />
      )}
    </>
  );
};

export default AlertButton;
